import { fmtDate, fmtDateTime } from '@/lib/dates.js';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldAlert, FileText, History } from 'lucide-react';
import { protocol, type ProtocolDocumentRow } from '@/api/client';
import { DragDropUpload } from '@/components/ui/DragDropUpload';
import { humanProcessingState } from '@/lib/humanise';
import { guessMime, sha256OfFile, uploadWithProgress } from '@/lib/s3Upload';
import { toast } from 'sonner';
import { errorText } from '@/lib/permissionError';


/**
 * DREAM-19 — Protocol Document Upload card.
 *
 * Lives inside DREAM-18 Step 2 (Protocol Setup). Drag-drop a PDF or DOCX
 * up to 50 MB; client computes SHA-256 (best-effort) for AC10 integrity
 * round-trip; FE then calls /presign → uploads directly to S3 via the
 * returned URL → calls /finalise. Polls status every 5 s until
 * processing_state moves out of in_progress.
 *
 * AC mapping:
 *   AC1  drag-drop visible
 *   AC2  PDF/DOCX only (DragDropUpload `accept`)
 *   AC3  50 MB cap (DragDropUpload `maxBytes`)
 *   AC4  name + size + "Checksum verified" status
 *   AC5  system-managed version label shown read-only
 *   AC6  previous-version history accessible
 *   AC7  S3 isolation + encryption + versioning (BE)
 *   AC8  audit panel entry (right rail of wizard)
 *   AC9  progress indicator while uploading
 *   AC10 server-side checksum verification (BE)
 *   AC11 auto-classified (BE)
 *   AC13 category protected (BE)
 *   AC14 eSource event emitted (BE no-op subscriber)
 *   AC15 async retry framework (BE)
 *   AC16 In progress / Completed / Needs attention indicator
 *   AC17 audit for retries with attempt count (BE)
 */

export function ProtocolUploadCard({ studyId }: { studyId: string }) {
  const qc = useQueryClient();
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const versionsQ = useQuery({
    queryKey: ['protocol-versions', studyId],
    queryFn: () => protocol.versions(studyId),
  });

  const currentVersion = versionsQ.data?.versions[0] ?? null;
  const trackedDocId = activeDocId ?? currentVersion?.document_id ?? null;

  // Poll status when there's a document being processed. AC16: pull every 5 s.
  const statusQ = useQuery({
    queryKey: ['protocol-status', studyId, trackedDocId],
    queryFn: () => protocol.status(studyId, trackedDocId!),
    enabled: !!trackedDocId,
    refetchInterval: (q) => {
      const doc = q.state.data?.document;
      if (!doc) return 5_000;
      return doc.processing_state === 'in_progress' ? 5_000 : false;
    },
  });

  const displayDoc: ProtocolDocumentRow | null =
    statusQ.data?.document ?? currentVersion ?? null;

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      // Step 1: get presigned URL + reserved document_id from the BE.
      const presigned = await protocol.presign(studyId, {
        file_name: file.name,
        mime_type: file.type || guessMime(file.name),
      });

      // Step 2: compute client-side SHA-256 for integrity round-trip (AC4 / AC10).
      const clientSha256 = await sha256OfFile(file);

      // Step 3: PUT to S3 with progress tracking (AC9).
      await uploadWithProgress(presigned.upload_url, file, file.type || guessMime(file.name), setUploadProgress);

      // Step 4: tell the BE the upload completed; it enqueues the verification job.
      // client_sha256 may be null on HTTP origins where crypto.subtle is undefined —
      // omit it then; the BE's server-side checksum is the real check.
      const result = await protocol.finalise(studyId, {
        document_id: presigned.document_id,
        s3_bucket: presigned.s3_bucket,
        s3_key: presigned.s3_key,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || guessMime(file.name),
        ...(clientSha256 ? { client_sha256: clientSha256 } : {}),
      });
      return result;
    },
    onSuccess: (res) => {
      toast.success(`Protocol uploaded as version ${res.version_label}`);
      setActiveDocId(res.document_id);
      setUploadProgress(null);
      qc.invalidateQueries({ queryKey: ['protocol-versions', studyId] });
    },
    onError: (err) => {
      setUploadProgress(null);
      toast.error(errorText(err, 'Upload failed'));
    },
  });

  const isUploading = uploadMut.isPending || (uploadProgress !== null && uploadProgress < 100);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-ink-muted" />
          <h3 className="text-sm font-semibold text-ink-primary">Protocol document</h3>
          {displayDoc?.version_label && (
            <span className="badge badge-info text-[10px]">Version {displayDoc.version_label}</span>
          )}
        </div>
        {(versionsQ.data?.count ?? 0) > 1 && (
          <button type="button" className="btn-ghost text-xs"
            onClick={() => setShowHistory((v) => !v)}>
            <History className="h-3.5 w-3.5" /> {showHistory ? 'Hide history' : 'Show history'} ({versionsQ.data!.count})
          </button>
        )}
      </div>

      {/* Current file summary */}
      {displayDoc && !isUploading && (
        <CurrentDocCard doc={displayDoc} />
      )}

      {/* Drop zone — visible whenever no upload is in flight */}
      {!isUploading && (
        <DragDropUpload
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          acceptHint="PDF or DOCX, max 50 MB"
          maxBytes={50 * 1024 * 1024}
          onSelect={(file) => uploadMut.mutate(file)}
          currentFileName={null}
        />
      )}

      {/* Upload progress (AC9) */}
      {isUploading && (
        <div className="card p-3">
          <div className="flex items-center gap-2 text-xs text-ink-muted mb-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Uploading… {uploadProgress !== null ? `${uploadProgress}%` : ''}
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded bg-surface-muted">
            <div className="h-full bg-accent-500 transition-all"
              style={{ width: `${uploadProgress ?? 0}%` }} />
          </div>
        </div>
      )}

      {/* History (AC6) */}
      {showHistory && (
        <VersionHistory versions={versionsQ.data?.versions ?? []} currentDocId={trackedDocId} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CurrentDocCard({ doc }: { doc: ProtocolDocumentRow }) {
  return (
    <div className="rounded-md border border-surface-border bg-surface-muted/40 p-3 text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-ink-primary">{doc.file_name}</div>
          <div className="text-[11px] text-ink-muted">
            {formatBytes(doc.file_size)} · uploaded {fmtDateTime(doc.uploaded_at)}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {/* Checksum is verified silently — no user-facing badge per
                [[dreamit2-hide-checksum-ui]]. Failures surface as normal
                upload errors ("Upload failed — the file may be corrupted"). */}
            <ProcessingStateBadge state={doc.processing_state} />
          </div>
          {doc.processing_state === 'needs_attention' && (
            <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
              <strong>Needs attention</strong> — the system is retrying automatically. You do not
              need to re-upload the file. {doc.last_error && <span>Reason: {doc.last_error}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProcessingStateBadge({ state }: { state: ProtocolDocumentRow['processing_state'] }) {
  if (state === 'completed') {
    return <span className="badge badge-success text-[10px]">{humanProcessingState(state)}</span>;
  }
  if (state === 'needs_attention') {
    return (
      <span className="inline-flex items-center gap-1 badge badge-warn text-[10px]">
        <ShieldAlert className="h-3 w-3" /> {humanProcessingState(state)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 badge badge-info text-[10px]">
      <Loader2 className="h-3 w-3 animate-spin" /> {humanProcessingState(state)}
    </span>
  );
}

function VersionHistory({ versions, currentDocId }: { versions: ProtocolDocumentRow[]; currentDocId: string | null }) {
  if (versions.length === 0) return null;
  return (
    <div className="card overflow-hidden">
      <div className="border-b border-surface-border bg-surface-muted/40 px-3 py-2 text-xs uppercase tracking-wider text-ink-muted">
        Version history
      </div>
      <ul className="divide-y divide-surface-border">
        {versions.map((v) => (
          <li key={v.document_id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="badge badge-info text-[10px]">v{v.version_label}</span>
              <span className="font-medium text-ink-primary">{v.file_name}</span>
              {v.document_id === currentDocId && (
                <span className="text-[10px] text-emerald-700">(current)</span>
              )}
            </div>
            <div className="text-[10px] text-ink-muted">
              {formatBytes(v.file_size)} · {fmtDate(v.uploaded_at)}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

